import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'
import { useAuth } from './contexts/useAuth'
import NavBar from './components/NavBar'
import AccessDenied from './components/AccessDenied'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import LoginSuccess from './pages/LoginSuccess'
import Home from './pages/Home'
import GenerateList from './pages/GenerateList/GenerateList'
import ShoppingMode from './pages/ShoppingMode'
import Inventory from './pages/Inventory'
import MyRecipes from './pages/MyRecipes'
import Profile from './pages/Profile'
import { useEffect, type ReactNode } from 'react'

/** Redirects to /login if user is neither authenticated nor guest */
function RequireAccess({ children }: { children: ReactNode }) {
  const { isAuthenticated, isGuest, loading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && !isAuthenticated && !isGuest) {
      navigate('/login', { replace: true })
    }
  }, [loading, isAuthenticated, isGuest, navigate])

  if (loading) return null
  if (!isAuthenticated && !isGuest) return null
  return <>{children}</>
}

/** Shows an access denied page for protected features when user is not authenticated */
function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) return null
  if (!isAuthenticated) {
    return (
      <AccessDenied
        title="Acces a l'inventaire refuse"
        description="Vous devez avoir un compte pour gerer votre inventaire personnel."
      />
    )
  }
  return <>{children}</>
}

function AppLayout() {
  const location = useLocation()
  const isPublicPage = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/login/success'

  return (
    <div className="app-layout">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: 'rgba(18, 24, 48, 0.85)',
            color: 'var(--text)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius)',
            fontFamily: 'Inter, sans-serif',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          },
        }}
      />
      {isPublicPage ? (
        <main className="fade-in" style={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/success" element={<LoginSuccess />} />
          </Routes>
        </main>
      ) : (
        <RequireAccess>
          <main className="page-content fade-in">
            <Routes>
              <Route path="/app" element={<Home />} />
              <Route path="/generate" element={<GenerateList />} />
              <Route path="/shopping/:id" element={<ShoppingMode />} />
              <Route path="/inventory" element={<RequireAuth><Inventory /></RequireAuth>} />
              <Route path="/recipes" element={<MyRecipes />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Routes>
          </main>
          <NavBar />
        </RequireAccess>
      )}
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppLayout />
      </AuthProvider>
    </BrowserRouter>
  )
}
